import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { markAsDirty, toggleControls } from '@heda/core-util';
import { Location } from '@angular/common';
import { MeasurementUnitService as MeasurementUnitManageService } from 'app/mdm/terminology/measurment-unit/services/measurement-unit.service';
import { MeasurementUnitService } from 'app/mdm/terminology-lib/services/measurement-unit.service';
import { AuthContext, EditableFlexTableComponent } from '@heda/core-hospital-ui';
import { saveAs } from 'file-saver';
import { MeasurementUnit, MeasurementUnitMapping } from 'app/mdm/terminology-lib/services/measurement-unit';

@Component({
  templateUrl: './measurement-unit-edit.component.html',
})
export class MeasurementUnitEditComponent implements OnInit {
  private editPrivileges = ['mdm.admin.edit'];
  public hasEditPrivileges = false;

  public unit: MeasurementUnit;
  public units: MeasurementUnit[] = [];
  public form: UntypedFormGroup;
  public defaultMapping = { systemValue: '1' };

  @ViewChild('mappingsTable') public mappingsTable: EditableFlexTableComponent<MeasurementUnitMapping>;
  @ViewChild('importMeasurementUnitInp') public importMeasurementUnitInp: ElementRef;

  constructor(
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private authContext: AuthContext,
    private measurementUnitManageService: MeasurementUnitManageService,
    private measurementUnitService: MeasurementUnitService,
    private location: Location
  ) {}

  public ngOnInit(): void {
    this.createForm();
    const id = this.route.snapshot.paramMap.get('measurementUnitId');
    if (id) {
      this.measurementUnitService.load(Number(id)).subscribe((mu) => {
        this.unit = mu;
        this.unit.mappings = this.unit.mappings || [];
        this.form.patchValue(mu);
      });
    } else {
      this.unit = new MeasurementUnit();
      this.unit.mappings = [];
    }
    this.measurementUnitService.query().subscribe((units) => (this.units = units));
    this.hasEditPrivileges = this.authContext.hasAnyPrivilege(this.editPrivileges);
  }

  private createForm(): void {
    this.form = this.fb.group({
      code: [null, Validators.required],
      names: [null, Validators.required],
      alias: [null, Validators.required],
      period: this.fb.group({
        lower: [null, Validators.required],
        upper: [null],
      }),
      rounding: [null, Validators.required],
      kind: [null, Validators.required],
      definitionUnit: [null],
    });
    this.form.get('kind').valueChanges.subscribe(() => {
      this.form.patchValue({ definitionUnit: null });
    });
    this.form.get('definitionUnit').valueChanges.subscribe((val) => {
      toggleControls(this.fb, this.form, !!val, { definitionValue: [null, Validators.required] });
    });
  }

  public save(): void {
    if (!this.form.valid || !this.mappingsTable.validate()) {
      markAsDirty(this.form);
      return;
    }
    const mu = Object.assign(this.unit, this.form.getRawValue());
    this.measurementUnitManageService.save(mu, this.unit.id).subscribe(() => this.location.back());
  }

  public sameKind(unit: MeasurementUnit, kind: string): boolean {
    return unit && unit.kind.code === kind;
  }

  public exportMeasurementUnit(): void {
    const mu = this.form.getRawValue();
    const out = { ...this.unit, ...mu, id: undefined };
    saveAs(new Blob([JSON.stringify(out, null, 2)], { type: 'application/json' }), `${mu.code}.json`);
  }

  public importMeasurementUnit(): void {
    this.importMeasurementUnitInp.nativeElement.click();
  }

  public readFile(event: any): void {
    const file = event.target.files[0];
    if (!file) {
      return;
    }
    const fileReader = new FileReader();
    fileReader.readAsText(file, 'UTF-8');
    fileReader.onload = () => {
      this.unit = { ...JSON.parse(fileReader.result as string), id: this.unit.id };
      this.form.patchValue(this.unit);
    };
  }
}
